import config from '../config';
import { centToCurrency } from './formatNumber';

const roles = config.roles;

/**
 * What a user is bound to, shown under their role in the Users grid: the
 * gateway's name for a gateway user. No other role binds a user to anything in
 * the backend (cabinet.users has only gateway_id), so for them it is empty.
 *
 * @param {Object} role - The user's role, with its systemRole.
 * @param {Object} data - The user row.
 * @returns {string}
 *
 * @example
 * getUserNameByRole({ systemRole: 'gateway' }, { gateway: { name: 'Bank A' } }); // → "Bank A"
 * getUserNameByRole({ systemRole: 'admin' }, {}); // → ""
 */
export const getUserNameByRole = (role, data) =>
    role?.systemRole === roles.gateway.systemRole ? data?.gateway?.name ?? '' : '';

/**
 * Retrieves the UUID of a transaction based on its kind.
 *
 * Supported kinds are:
 * - `'payment'`: returns `data.payment.uuid`
 * - `'p2p'`: returns `data.p2p.uuid`
 * - `'payout'`: returns `data.payout.uuid`
 *
 * If the kind does not match any of the supported transaction types,
 * an empty string is returned.
 *
 * @function getUuidTransactionByKind
 * @param {string} kind - The type of transaction (`'payment'` | `'p2p'` | `'payout'`).
 *                        This is a string identifying the transaction category.
 * @param {Object} data - The full transaction data object.
 * @param {Object} [data.payment] - Payment transaction data.
 * @param {string} [data.payment.uuid] - UUID of the payment transaction.
 * @param {Object} [data.p2p] - P2P transaction data.
 * @param {string} [data.p2p.uuid] - UUID of the P2P transaction.
 * @param {Object} [data.payout] - Payout transaction data.
 * @param {string} [data.payout.uuid] - UUID of the payout transaction.
 * @returns {string} The UUID string corresponding to the specified kind, or an empty string if unavailable.
 *
 * @example
 * getUuidTransactionByKind('payment', transactionData); // → "a1b2c3d4..."
 * getUuidTransactionByKind('p2p', transactionData);     // → "d4c3b2a1..."
 * getUuidTransactionByKind('invalid', transactionData); // → ""
 */
export const getUuidTransactionByKind = (kind, data) => {
    const map = {
        payment: data?.payment?.uuid,
        p2p: data?.p2p?.uuid,
        payout: data?.payout?.uuid
    };
    return map[kind] ?? '';
};

export const getCostType = (type, data) => {
    const agentDisplay = [
        data?.agent?.name ? `agent: ${data.agent.name}` : null,
        data?.gateway?.name ? `gateway: ${data.gateway.name}` : null,
        data?.terminal?.name ? `terminal: ${data.terminal.name}` : null,
    ]
        .filter(Boolean)
        .join('; ');


    const map = {
        merchant: data?.terminal?.name,
        gateway: data?.gateway?.name,
        agent: agentDisplay,
    };
    return map[type] ?? '';
};

export const getTotalCommission = (id, data) => {
    return data[id];
};

export const getFeeByDimension = (dimension, data) => {
    if (dimension === 'fixed') {
        return centToCurrency(data.fee);
    } else if (dimension === 'percent') {
        return data?.fee ? `${data.fee}%` : '';
    }
};
